'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  ListTree,
  Maximize2,
  Minimize2,
  Printer,
  Quote,
  Search,
  Type,
  X,
} from 'lucide-react'
import {
  BOOKMARKS_STORAGE_KEY,
  RECENT_STORAGE_KEY,
  type SavedResourceItem,
} from './LibraryRecentBar'

export interface VolumeTreeNode {
  id: string
  ssId: number
  parentSsId: number
  title: string
  menuTitle: string
  canonicalPath: string
  kind: 'root' | 'book' | 'section' | 'article'
  sortOrder: number
  children: VolumeTreeNode[]
}

export interface FlatEntry {
  id: string
  ssId: number
  title: string
  menuTitle: string
  canonicalPath: string
  kind: 'root' | 'book' | 'section' | 'article'
  depth: number
}

export interface VolumeSummary {
  id: string
  roman: string
  title: string
  canonicalPath: string
  slug: string
}

interface LibraryReaderClientProps {
  currentDoc: {
    id: string
    ssId?: number
    title: string
    menuTitle?: string
    canonicalPath: string
    kind: 'root' | 'book' | 'section' | 'article'
    bookTitle?: string
    bookSlug?: string
    authorTags?: string
    source?: string
  }
  isUnlocked: boolean
  volumes: VolumeSummary[]
  volumeRoot: VolumeTreeNode | null
  flatSequence: FlatEntry[]
  activeLineageSsIds: number[]
  children: React.ReactNode
  bottomChildrenToc?: React.ReactNode
}

type FontScale = 'sm' | 'md' | 'lg' | 'xl'
const FONT_SIZE_MAP: Record<FontScale, string> = {
  sm: '1.0rem',
  md: '1.1rem',
  lg: '1.22rem',
  xl: '1.36rem',
}

const PREFS_STORAGE_KEY = 'wlib_reader_prefs_v1'

export function LibraryReaderClient({
  currentDoc,
  isUnlocked,
  volumes,
  volumeRoot,
  flatSequence,
  activeLineageSsIds,
  children,
  bottomChildrenToc,
}: LibraryReaderClientProps) {
  const router = useRouter()
  const [mobileTocOpen, setMobileTocOpen] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set(activeLineageSsIds))
  const [fontScale, setFontScale] = useState<FontScale>('md')
  const [focusMode, setFocusMode] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [citeOpen, setCiteOpen] = useState(false)
  const [citeFormat, setCiteFormat] = useState<'chicago' | 'mla' | 'apa'>('chicago')
  const [copiedCite, setCopiedCite] = useState(false)

  const activeNodeRef = useRef<HTMLAnchorElement | null>(null)

  // Keep active lineage expanded whenever currentDoc changes
  useEffect(() => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      for (const id of activeLineageSsIds) {
        next.add(id)
      }
      return next
    })
    setMobileTocOpen(false)
    setCiteOpen(false)
  }, [currentDoc.canonicalPath, activeLineageSsIds])

  // Auto-scroll active item in sidebar into view on mount / route change
  useEffect(() => {
    if (activeNodeRef.current) {
      activeNodeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [currentDoc.canonicalPath, mobileTocOpen])

  // Load reader preferences & record in "Continue Reading" history
  useEffect(() => {
    try {
      const rawPrefs = localStorage.getItem(PREFS_STORAGE_KEY)
      if (rawPrefs) {
        const parsed = JSON.parse(rawPrefs)
        if (parsed.fontScale && FONT_SIZE_MAP[parsed.fontScale as FontScale]) {
          setFontScale(parsed.fontScale)
        }
        if (typeof parsed.focusMode === 'boolean') {
          setFocusMode(parsed.focusMode)
        }
      }

      // Check bookmark state
      const rawBookmarks = localStorage.getItem(BOOKMARKS_STORAGE_KEY)
      const bookmarks: SavedResourceItem[] = rawBookmarks ? JSON.parse(rawBookmarks) : []
      setIsBookmarked(bookmarks.some((b) => b.canonicalPath === currentDoc.canonicalPath))

      // Record in recent reading history (skip root)
      if (currentDoc.kind !== 'root') {
        const rawRecent = localStorage.getItem(RECENT_STORAGE_KEY)
        const recent: SavedResourceItem[] = rawRecent ? JSON.parse(rawRecent) : []
        const entry: SavedResourceItem = {
          canonicalPath: currentDoc.canonicalPath,
          title: currentDoc.title,
          bookTitle: currentDoc.bookTitle,
          kind: currentDoc.kind,
          timestamp: Date.now(),
        }
        const deduped = [
          entry,
          ...recent.filter((r) => r.canonicalPath !== currentDoc.canonicalPath),
        ].slice(0, 8)
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(deduped))
      }
    } catch {
      // Ignore storage errors
    }
  }, [currentDoc])

  const updatePrefs = (nextFont: FontScale, nextFocus: boolean) => {
    setFontScale(nextFont)
    setFocusMode(nextFocus)
    try {
      localStorage.setItem(
        PREFS_STORAGE_KEY,
        JSON.stringify({ fontScale: nextFont, focusMode: nextFocus }),
      )
    } catch {
      // Ignore
    }
  }

  const toggleBookmark = () => {
    try {
      const raw = localStorage.getItem(BOOKMARKS_STORAGE_KEY)
      const list: SavedResourceItem[] = raw ? JSON.parse(raw) : []
      const exists = list.some((b) => b.canonicalPath === currentDoc.canonicalPath)
      let next: SavedResourceItem[]
      if (exists) {
        next = list.filter((b) => b.canonicalPath !== currentDoc.canonicalPath)
        setIsBookmarked(false)
      } else {
        next = [
          {
            canonicalPath: currentDoc.canonicalPath,
            title: currentDoc.title,
            bookTitle: currentDoc.bookTitle,
            kind: currentDoc.kind,
            timestamp: Date.now(),
          },
          ...list,
        ]
        setIsBookmarked(true)
      }
      localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Ignore
    }
  }

  // Compute current index, prevEntry, and nextEntry in the volume's reading sequence
  const { currentIndex, prevEntry, nextEntry } = useMemo(() => {
    const idx = flatSequence.findIndex((item) => item.canonicalPath === currentDoc.canonicalPath)
    return {
      currentIndex: idx,
      prevEntry: idx > 0 ? flatSequence[idx - 1] : null,
      nextEntry: idx >= 0 && idx < flatSequence.length - 1 ? flatSequence[idx + 1] : null,
    }
  }, [flatSequence, currentDoc.canonicalPath])

  // Keyboard arrow navigation (Alt+Left / Alt+Right or Left / Right when not typing)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.metaKey || e.ctrlKey || e.shiftKey) return

      if (e.key === 'ArrowLeft' && prevEntry) {
        router.push(prevEntry.canonicalPath)
      } else if (e.key === 'ArrowRight' && nextEntry) {
        router.push(nextEntry.canonicalPath)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [prevEntry, nextEntry, router])

  // Citation generator strings
  const citations = useMemo(() => {
    const author = currentDoc.authorTags || 'Robert E. Webber, ed.'
    const title = currentDoc.title
    const volume = currentDoc.bookTitle || 'The Complete Library of Christian Worship'
    const year = '1993–1994'
    const url =
      typeof window !== 'undefined'
        ? `${window.location.origin}${currentDoc.canonicalPath}`
        : `https://wlib.pcdev.cc${currentDoc.canonicalPath}`

    return {
      chicago: `${author}. "${title}." In ${volume}, edited by Robert E. Webber. The Complete Library of Christian Worship. Peabody, MA: Hendrickson Publishers, ${year}. ${url}.`,
      mla: `${author}. "${title}." ${volume}, edited by Robert E. Webber, Hendrickson Publishers, ${year}. Worship Library, ${url}.`,
      apa: `${author}. (1994). ${title}. In R. E. Webber (Ed.), ${volume}. Hendrickson Publishers. ${url}`,
    }
  }, [currentDoc])

  const handleCopyCitation = () => {
    const text = citations[citeFormat]
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
      setCopiedCite(true)
      setTimeout(() => setCopiedCite(false), 2000)
    }
  }

  const toggleNode = (ssId: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(ssId)) {
        next.delete(ssId)
      } else {
        next.add(ssId)
      }
      return next
    })
  }

  // Filter matching for the sidebar search box
  const normalizedQuery = filterQuery.trim().toLowerCase()
  const filteredFlat = useMemo(() => {
    if (!normalizedQuery) return []
    return flatSequence.filter(
      (item) =>
        item.title.toLowerCase().includes(normalizedQuery) ||
        item.menuTitle.toLowerCase().includes(normalizedQuery),
    )
  }, [flatSequence, normalizedQuery])

  // Recursive Tree Node renderer
  const renderTreeNode = (node: VolumeTreeNode, depth = 0) => {
    const isCurrent = node.canonicalPath === currentDoc.canonicalPath
    const isInLineage = activeLineageSsIds.includes(node.ssId)
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedIds.has(node.ssId)

    return (
      <li key={node.id} className="wl-toc-node">
        <div
          className={`wl-toc-row ${isCurrent ? 'active' : isInLineage ? 'in-lineage' : ''}`}
          style={{ paddingLeft: `${Math.min(depth * 0.75, 2.25) + 0.45}rem` }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="wl-toc-expand-btn"
              onClick={(e) => toggleNode(node.ssId, e)}
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="wl-toc-bullet" aria-hidden="true" />
          )}

          <Link
            href={node.canonicalPath}
            ref={isCurrent ? activeNodeRef : undefined}
            className={`wl-toc-link ${node.kind === 'section' ? 'is-section' : 'is-article'}`}
            onClick={() => setMobileTocOpen(false)}
          >
            <span>{node.menuTitle || node.title}</span>
            {hasChildren && (
              <span className="wl-toc-count" title={`${node.children.length} items`}>
                {node.children.length}
              </span>
            )}
          </Link>
        </div>

        {hasChildren && isExpanded && (
          <ul className="wl-toc-children">
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </ul>
        )}
      </li>
    )
  }

  // Reusable TOC Content rendered in both Desktop Sidebar and Mobile Slide-Over Drawer
  const renderTocPanel = () => (
    <div className="wl-toc-panel">
      {/* Top Link + Section Switcher */}
      <div className="wl-toc-header">
        <Link
          href="/library"
          className="wl-toc-all-volumes"
          onClick={() => setMobileTocOpen(false)}
        >
          <ArrowLeft size={13} />
          <span>Library Home</span>
        </Link>

        <label htmlFor="wl-volume-select" className="wl-sr-only">
          Switch Library Section
        </label>
        <select
          id="wl-volume-select"
          className="wl-toc-volume-select"
          value={volumeRoot?.canonicalPath || currentDoc.canonicalPath}
          onChange={(e) => {
            if (e.target.value) {
              setMobileTocOpen(false)
              router.push(e.target.value)
            }
          }}
        >
          {volumes.map((v) => (
            <option key={v.id} value={v.canonicalPath}>
              {v.title}
            </option>
          ))}
        </select>
      </div>

      {/* Quick Filter Input */}
      <div className="wl-toc-search">
        <Search size={14} className="wl-toc-search-icon" />
        <input
          type="search"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder="Filter topics & articles in this section..."
          className="wl-toc-search-input"
        />
        {filterQuery && (
          <button
            type="button"
            onClick={() => setFilterQuery('')}
            className="wl-toc-search-clear"
            aria-label="Clear filter"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Expand / Collapse All Controls */}
      {!normalizedQuery && volumeRoot && volumeRoot.children.length > 0 && (
        <div className="wl-toc-tree-controls">
          <span>
            {currentIndex >= 0
              ? `Reading ${currentIndex + 1} of ${flatSequence.length}`
              : `${flatSequence.length} entries`}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              className="wl-toc-tiny-btn"
              onClick={() => {
                const allIds = new Set<number>()
                const walk = (n: VolumeTreeNode) => {
                  if (n.children.length > 0) {
                    allIds.add(n.ssId)
                    n.children.forEach(walk)
                  }
                }
                walk(volumeRoot)
                setExpandedIds(allIds)
              }}
            >
              Expand All
            </button>
            <span>·</span>
            <button
              type="button"
              className="wl-toc-tiny-btn"
              onClick={() => setExpandedIds(new Set(activeLineageSsIds))}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Filtered List or Full Collapsible Volume Tree */}
      <div className="wl-toc-scroll-area">
        {normalizedQuery ? (
          filteredFlat.length > 0 ? (
            <ul className="wl-toc-root-list">
              {filteredFlat.map((item) => {
                const isCurrent = item.canonicalPath === currentDoc.canonicalPath
                return (
                  <li key={item.id} className="wl-toc-node">
                    <div className={`wl-toc-row ${isCurrent ? 'active' : ''}`}>
                      <span className="wl-toc-bullet" />
                      <Link
                        href={item.canonicalPath}
                        className={`wl-toc-link ${item.kind === 'section' ? 'is-section' : 'is-article'}`}
                        onClick={() => setMobileTocOpen(false)}
                      >
                        <span>{item.menuTitle || item.title}</span>
                      </Link>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div style={{ padding: '1rem 0.5rem', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
              No chapters or articles in this section match “{filterQuery}”.
            </div>
          )
        ) : volumeRoot ? (
          <ul className="wl-toc-root-list">
            {/* Section Overview Item */}
            <li className="wl-toc-node">
              <div
                className={`wl-toc-row ${volumeRoot.canonicalPath === currentDoc.canonicalPath ? 'active' : ''}`}
              >
                <BookOpen
                  size={14}
                  style={{ color: 'var(--purple-primary)', flexShrink: 0, marginRight: 4 }}
                />
                <Link
                  href={volumeRoot.canonicalPath}
                  className="wl-toc-link is-section"
                  onClick={() => setMobileTocOpen(false)}
                >
                  <span>Section Overview &amp; Contents</span>
                </Link>
              </div>
            </li>
            {volumeRoot.children.map((sectionNode) => renderTreeNode(sectionNode, 0))}
          </ul>
        ) : null}
      </div>
    </div>
  )

  return (
    <div
      className="wl-reader-shell"
      style={{ '--wl-reader-font-size': FONT_SIZE_MAP[fontScale] } as React.CSSProperties}
    >
      {/* Mobile Table of Contents Bar (Visible on Phone/Tablet < 1024px) */}
      <div className="wl-mobile-reader-bar no-print">
        <button
          type="button"
          className="wl-mobile-toc-trigger"
          onClick={() => setMobileTocOpen(true)}
        >
          <ListTree size={16} style={{ color: 'var(--purple-primary)', flexShrink: 0 }} />
          <span>Table of Contents</span>
        </button>

        <div className="wl-mobile-reader-quick-nav">
          {prevEntry ? (
            <Link
              href={prevEntry.canonicalPath}
              className="wl-mobile-step-btn"
              title={`Previous: ${prevEntry.title}`}
            >
              <ArrowLeft size={14} />
              <span>Prev</span>
            </Link>
          ) : (
            <span className="wl-mobile-step-btn disabled">
              <ArrowLeft size={14} />
              <span>Prev</span>
            </span>
          )}

          {nextEntry ? (
            <Link
              href={nextEntry.canonicalPath}
              className="wl-mobile-step-btn"
              title={`Next: ${nextEntry.title}`}
            >
              <span>Next</span>
              <ArrowRight size={14} />
            </Link>
          ) : (
            <span className="wl-mobile-step-btn disabled">
              <span>Next</span>
              <ArrowRight size={14} />
            </span>
          )}
        </div>
      </div>

      {/* Mobile Slide-Over Table of Contents Drawer */}
      {mobileTocOpen && (
        <div
          className="wl-mobile-toc-backdrop no-print"
          onClick={() => setMobileTocOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Library Table of Contents"
        >
          <div className="wl-mobile-toc-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="wl-mobile-toc-sheet-top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BookOpen size={16} style={{ color: 'var(--purple-primary)' }} />
                <strong style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem' }}>
                  Library Navigation
                </strong>
              </div>
              <button
                type="button"
                className="wl-mobile-toc-close"
                onClick={() => setMobileTocOpen(false)}
                aria-label="Close Contents"
              >
                <X size={18} />
              </button>
            </div>
            {renderTocPanel()}
          </div>
        </div>
      )}

      {/* Main Desktop 2-Column (or Focus Mode 1-Column) Reader Grid */}
      <div className={`wl-reader-layout ${focusMode ? 'focus-mode' : ''}`}>
        {!focusMode && (
          <aside className="wl-reader-sidebar no-print" aria-label="Volume & Chapter Navigation">
            {renderTocPanel()}
          </aside>
        )}

        <article className="wl-reader-main">
          {/* Authenticated Scholarly Reader Utility Bar */}
          {isUnlocked && (
            <div className="wl-scholar-toolbar no-print">
              <div className="wl-scholar-toolbar-left">
                {/* Font Size Controls */}
                <div className="wl-font-controls" role="group" aria-label="Reading Text Size">
                  <Type size={13} style={{ color: 'var(--text-muted)', marginRight: 2 }} />
                  {(['sm', 'md', 'lg', 'xl'] as FontScale[]).map((scale, i) => (
                    <button
                      key={scale}
                      type="button"
                      className={`wl-font-btn ${fontScale === scale ? 'active' : ''}`}
                      onClick={() => updatePrefs(scale, focusMode)}
                      title={`Text size ${scale.toUpperCase()}`}
                    >
                      {i === 0 ? 'A-' : i === 1 ? 'A' : i === 2 ? 'A+' : 'A++'}
                    </button>
                  ))}
                </div>

                {/* Desktop Focus / Wide Mode Toggle */}
                <button
                  type="button"
                  className={`wl-toolbar-btn wl-desktop-only ${focusMode ? 'active' : ''}`}
                  onClick={() => updatePrefs(fontScale, !focusMode)}
                  title={focusMode ? 'Show Chapter Sidebar' : 'Focus Mode (Hide Sidebar)'}
                >
                  {focusMode ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                  <span>{focusMode ? 'Show Sidebar' : 'Focus Mode'}</span>
                </button>
              </div>

              <div className="wl-scholar-toolbar-right">
                {/* Save Bookmark */}
                <button
                  type="button"
                  className={`wl-toolbar-btn ${isBookmarked ? 'active' : ''}`}
                  onClick={toggleBookmark}
                >
                  {isBookmarked ? <BookmarkCheck size={13} /> : <Bookmark size={13} />}
                  <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                </button>

                {/* Cite This Article */}
                <button
                  type="button"
                  className={`wl-toolbar-btn ${citeOpen ? 'active' : ''}`}
                  onClick={() => setCiteOpen((prev) => !prev)}
                >
                  <Quote size={13} />
                  <span>Cite</span>
                </button>

                {/* Print / PDF Citation View */}
                {typeof currentDoc.ssId === 'number' && (
                  <Link
                    href={`/pr/page/${currentDoc.ssId}`}
                    className="wl-toolbar-btn"
                    title="Open Clean Citation & Print View"
                  >
                    <Printer size={13} />
                    <span>Print</span>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Expandable Academic Citation Box */}
          {citeOpen && (
            <div className="wl-citation-box no-print">
              <div className="wl-citation-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Quote size={14} style={{ color: 'var(--purple-primary)' }} />
                  <strong>Academic Citation Generator</strong>
                </div>
                <div className="wl-citation-tabs">
                  {(['chicago', 'mla', 'apa'] as const).map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      className={`wl-citation-tab ${citeFormat === fmt ? 'active' : ''}`}
                      onClick={() => setCiteFormat(fmt)}
                    >
                      {fmt === 'chicago' ? 'Chicago / Turabian' : fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="wl-citation-body">{citations[citeFormat]}</div>
              <div className="wl-citation-footer">
                <button
                  type="button"
                  className="wl-btn wl-btn-primary wl-btn-sm"
                  onClick={handleCopyCitation}
                >
                  {copiedCite ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copiedCite ? 'Copied to Clipboard' : 'Copy Citation'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Main Article Header + Prose Content passed from Server */}
          {children}

          {/* Sequential "Previous / Next" Book Pagination Bar */}
          {(prevEntry || nextEntry) && (
            <nav className="wl-seq-nav no-print" aria-label="Sequential Chapter Navigation">
              {prevEntry ? (
                <Link href={prevEntry.canonicalPath} className="wl-seq-card prev">
                  <div className="wl-seq-direction">
                    <ArrowLeft size={14} />
                    <span>
                      Previous {prevEntry.kind === 'section' ? 'Section / Chapter' : 'Article'}
                    </span>
                  </div>
                  <div className="wl-seq-title">{prevEntry.title}</div>
                </Link>
              ) : (
                <div />
              )}

              {nextEntry ? (
                <Link href={nextEntry.canonicalPath} className="wl-seq-card next">
                  <div className="wl-seq-direction">
                    <span>
                      Next {nextEntry.kind === 'section' ? 'Section / Chapter' : 'Article'}
                    </span>
                    <ArrowRight size={14} />
                  </div>
                  <div className="wl-seq-title">{nextEntry.title}</div>
                </Link>
              ) : (
                <div />
              )}
            </nav>
          )}

          {/* Child Sections / Articles Table of Contents at Bottom */}
          {bottomChildrenToc}
        </article>
      </div>
    </div>
  )
}
